// The parts of an output page, drawn over it as boxes: what each thing on the page is and where it
// comes from. The output's builder records a page's parts in page points (the Saju book:
// build-product-samples.py Book.parts): each text with the addresses it is edited by (`edits`), the
// app components captured into it, its pictures. The output says what its kinds of part are and
// how they are drawn (output.partKinds: { kind: { label, note, color, outline? } }); the page's
// texts are listed and edited beside it (output-page-texts.mjs).

const SVG = 'http://www.w3.org/2000/svg';
const ROLE_NAMES = { background: '배경', frame: '틀', plaque: '명판', icon: '아이콘', image: '그림' };

const fetched = new Map();
/**
 * A page's parts: { size: [w, h], parts: [...], elements, layouts, layoutPages } — carried by the page, or
 * from its book's parts file. `elements` are the layout elements (kept as data) the page is drawn from,
 * `layouts` what they may change and what the layouts are, `layoutPages` how many of the book's pages each draws.
 */
export async function partsOf(page) {
  if (page.parts) return { size: page.pageSize ?? [760, 1078], parts: page.parts, elements: page.elements ?? [], layoutId: page.layoutId ?? null, layouts: page.layouts ?? null, layoutPages: page.layoutPages ?? {} };
  if (!page.partsUrl) return null;
  if (!fetched.has(page.partsUrl)) {
    fetched.set(page.partsUrl, fetch(page.partsUrl).then(response => {
      if (!response.ok) throw new Error(`구성 정보를 불러오지 못했습니다 (${response.status}).`);
      return response.json();
    }).catch(error => { fetched.delete(page.partsUrl); throw error; }));
  }
  const book = await fetched.get(page.partsUrl);
  const layoutPages = {};
  for (const name of Object.values(book.layoutIds)) layoutPages[name] = (layoutPages[name] ?? 0) + 1;
  return { size: book.pageSize, parts: book.pages[String(page.number)] ?? [], elements: book.elements?.[String(page.number)] ?? [], layoutId: book.layoutIds?.[String(page.number)] ?? null, layouts: book.layouts ?? null, layoutPages };
}
export const hasParts = page => Boolean(page?.parts || page?.partsUrl);

/** A kind of part as the output names it; a kind it does not name is named by itself. */
export const kindOf = (kinds, kind) => kinds?.[kind] ?? { label: kind, note: '', color: '#7c858f' };
/** A part's box is drawn from the page's background up: the page's own background is no part to point at. */
export const drawn = part => !(part.kind === 'image' && part.role === 'background');

function describe(part) {
  if (part.kind === 'component') return { title: part.components.map(file => file.split('/').pop()).join(' + '), detail: `데이터 ${part.data} · ${part.asset}` };
  if (part.kind === 'image') return { title: `${ROLE_NAMES[part.role] ?? '그림'} · ${part.asset}`, detail: '' };
  return { title: part.label, detail: part.text ?? '' };
}

/**
 * draw(figure, { size, parts }, { kinds, editable(part), edited(part), onPick(index), onHover(index, on) })
 * draws the parts over the page's picture: the ones edited beside the page are marked, and those with
 * an edit waiting; a click on a box is onPick, the pointer over it onHover.
 */
export function mountPartsOverlay() {
  let overlay = null, shown = [];
  function draw(figure, { size: [width, height], parts }, { kinds = {}, editable = () => false, edited = () => false, onPick = () => {}, onHover = () => {} } = {}) {
    clear();
    shown = parts;
    // Pictures first, so the text and components drawn over them stay on top and clickable.
    const order = parts.map((part, index) => ({ part, index })).sort((a, b) => (a.part.kind === 'image') - (b.part.kind === 'image')).reverse();
    overlay = document.createElementNS(SVG, 'svg');
    overlay.setAttribute('class', 'es-output-parts-overlay');
    overlay.setAttribute('viewBox', `0 0 ${width} ${height}`);
    overlay.setAttribute('preserveAspectRatio', 'none');
    overlay.dataset.testid = 'studio-output-parts-overlay';
    for (const { part, index } of order.filter(({ part }) => drawn(part))) {
      const kind = kindOf(kinds, part.kind);
      const rect = document.createElementNS(SVG, 'rect');
      for (const [name, value] of Object.entries({ x: part.x, y: part.y, width: part.w, height: part.h })) rect.setAttribute(name, String(value));
      rect.setAttribute('class', ['es-part', `es-part--${part.kind}`, kind.outline && 'es-part--outline', editable(part) && 'es-part--editable', edited(part) && 'es-part--edited'].filter(Boolean).join(' '));
      rect.style.setProperty('--part-color', kind.color);
      rect.dataset.part = String(index);
      const title = document.createElementNS(SVG, 'title');
      const { title: name, detail } = describe(part);
      title.textContent = `${kind.label} · ${name}${detail ? `\n${detail}` : ''}${editable(part) ? '\n눌러서 오른쪽에서 고칩니다' : ''}`;
      rect.append(title);
      rect.addEventListener('mouseenter', () => { highlight(index, true); onHover(index, true); });
      rect.addEventListener('mouseleave', () => { highlight(index, false); onHover(index, false); });
      rect.addEventListener('click', () => onPick(index));
      overlay.append(rect);
    }
    figure.querySelector('.es-output-reader-frame').append(overlay);
  }
  function highlight(index, on) { overlay?.querySelector(`[data-part="${index}"]`)?.classList.toggle('es-part--lit', on); }
  /** Marks the boxes of the parts printing `address` edited or not, after it was saved or undone beside the page. */
  function markAddress(address, edited) {
    shown.forEach((part, index) => { if ((part.edits ?? []).includes(address)) overlay?.querySelector(`[data-part="${index}"]`)?.classList.toggle('es-part--edited', edited); });
  }
  function clear() { overlay?.remove(); overlay = null; shown = []; }
  return { draw, highlight, markAddress, clear };
}
